import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import {
  claimPaywallError,
  isPaymentRequiredError,
  isRefusalFromEarlierView,
  registerPaywallPresenter,
} from '@personal-trainer-app/shared';
import { dayjs } from '@personal-trainer-app/shared/src/utils/date';

import { PaywallModal } from '../PaywallModal';

import type {
  PaywallContextValue,
  PaywallGateRegistryValue,
  PaywallPageContextValue,
  PaywallProviderProps,
} from './PaywallProvider.types';
import type { PaymentRequiredError } from '@personal-trainer-app/shared';

const PaywallContext = createContext<PaywallContextValue | null>(null);
const PaywallPageContext = createContext<PaywallPageContextValue | null>(null);
const PaywallGateRegistryContext =
  createContext<PaywallGateRegistryValue | null>(null);

/**
 * How long after a navigation a `GET` refusal still counts as the page's
 * initial load (feature-gate PRD, D3). Inside the window the routed view is
 * replaced — the page has nothing behind its skeleton to preserve. Past it,
 * the 402 came from a refetch on a page the reader is using, and destroying
 * that page is the one unrecoverable presentation — so it downgrades to the
 * modal, which the PRD prefers wherever the distinction is uncertain.
 */
const INITIAL_LOAD_WINDOW_MS = 5000;

/**
 * PaywallProvider — the web paywall presenter (feature-gate PRD, D2).
 *
 * Mounted once inside the authenticated layout. On mount it registers itself
 * into the shared DI container, so every 402 the request coordinator sees is
 * presented here without any surface opting in. Its whole decision is the
 * read/mutation split:
 *
 * - a `GET` sent before the last navigation is dropped — its page is gone;
 * - a `GET` refused while a `PaywallGate` is mounted is left to the gates —
 *   the page has declared that it locks its own reads, panel by panel;
 * - otherwise a `GET` refused during a page's initial load replaces the routed
 *   view (via `PaywallRouteGate` consuming `PaywallPageContext`);
 * - everything else opens the app-level `PaywallModal` over the intact page.
 *
 * `usePaywall().showPaywall(err)` remains for mutation handlers that want the
 * modal synchronously and legibly at the call site — it is no longer required,
 * merely allowed.
 */
export function PaywallProvider({
  children,
}: PaywallProviderProps): React.JSX.Element {
  const [modalError, setModalError] = useState<PaymentRequiredError | null>(
    null
  );
  const [pageError, setPageError] = useState<PaymentRequiredError | null>(null);
  const location = useLocation();

  // A layout effect, so the stamp lands before the new page's data effects
  // start fetching — its own first reads must never look older than it.
  const lastNavigationAtRef = useRef(dayjs().valueOf());
  useLayoutEffect(() => {
    lastNavigationAtRef.current = dayjs().valueOf();
  }, [location.key]);

  // The presenter outlives React's commit cycle (it is invoked from a timer in
  // the shared publisher), and the DI container has no unregister — so guard
  // every presentation on mount state instead.
  /**
   * How many `PaywallGate`s are mounted. A gate claims the refusal it renders,
   * but only once React re-renders its panel — which can land after the
   * publisher's deferred presentation, and a `GET` near navigation would then
   * replace the whole view (the Reports hub read as one upgrade page, cycling
   * through every refused feature). Registration is known up front: gates
   * mount with the page, before its data effects start fetching.
   */
  const mountedGatesRef = useRef(0);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Returns whether something was shown; a drop (`false`) leaves the feature's
  // slug free in the publisher for the current page's own refusal.
  const present = useCallback((error: PaymentRequiredError): boolean => {
    if (!mountedRef.current) return false;

    // A read the previous page sent, refused after the reader left it.
    if (isRefusalFromEarlierView(error, lastNavigationAtRef.current)) {
      return false;
    }

    const method = (error as PaymentRequiredError & { method?: string }).method;

    // A page with gates locks its own reads; its non-read actions still get
    // the modal below, and its GET-shaped actions call `showPaywall` locally.
    if (method === 'GET' && mountedGatesRef.current > 0) return true;

    const isInitialLoadRead =
      method === 'GET' &&
      dayjs().valueOf() - lastNavigationAtRef.current < INITIAL_LOAD_WINDOW_MS;

    if (isInitialLoadRead) {
      setPageError(error);
      return true;
    }

    setModalError(error);
    return true;
  }, []);

  useEffect(() => {
    registerPaywallPresenter({ present });
  }, [present]);

  const showPaywall = useCallback((err: unknown): boolean => {
    if (isPaymentRequiredError(err)) {
      // A call-site modal IS a local handling, so it claims like a gate does:
      // the deferred global presentation was handed this same instance by the
      // coordinator and would otherwise present it a second time — a
      // redundant modal, or a view replacement underneath this one when a
      // GET-shaped action (a report download, say) 402s near a navigation.
      claimPaywallError(err);
      setModalError(err);
      return true;
    }
    return false;
  }, []);

  const closePaywall = useCallback(() => {
    setModalError(null);
  }, []);

  const clearPagePaywall = useCallback(() => {
    setPageError(null);
  }, []);

  const value = useMemo<PaywallContextValue>(
    () => ({ showPaywall }),
    [showPaywall]
  );

  const pageValue = useMemo<PaywallPageContextValue>(
    () => ({ pageError, clearPagePaywall }),
    [pageError, clearPagePaywall]
  );

  const registerGate = useCallback(() => {
    mountedGatesRef.current += 1;
    return () => {
      mountedGatesRef.current -= 1;
    };
  }, []);

  const gateRegistry = useMemo<PaywallGateRegistryValue>(
    () => ({ registerGate }),
    [registerGate]
  );

  return (
    <PaywallContext.Provider value={value}>
      <PaywallPageContext.Provider value={pageValue}>
        <PaywallGateRegistryContext.Provider value={gateRegistry}>
          {children}
          <PaywallModal error={modalError} onClose={closePaywall} />
        </PaywallGateRegistryContext.Provider>
      </PaywallPageContext.Provider>
    </PaywallContext.Provider>
  );
}

/**
 * Access the app-level paywall modal. Call `showPaywall(err)` in a mutation
 * handler's catch: it opens the modal and returns `true` for a `402`, or
 * returns `false` so the caller keeps its existing error path.
 *
 * Since the global presenter (feature-gate PRD) this is optional — an
 * unhandled mutation 402 opens the same modal automatically — but it stays
 * supported: one legible line at the call site, and the modal opens a frame
 * sooner than the deferred global presentation.
 *
 * Must be called within a `PaywallProvider`.
 *
 * @example
 * ```tsx
 * const { showPaywall } = usePaywall();
 * // ...
 * catch (err) {
 *   if (!showPaywall(err)) setAlert(getErrorMessage(err));
 * }
 * ```
 */
export function usePaywall(): PaywallContextValue {
  const context = useContext(PaywallContext);
  if (!context) {
    throw new Error('usePaywall must be used within a PaywallProvider');
  }
  return context;
}

/**
 * The view-replacing presentation, read by `PaywallRouteGate`. Deliberately
 * non-throwing: the gate sits inside `PrivateLayout`, which stories and tests
 * render without a `PaywallProvider` — outside one it returns `null` and the
 * gate becomes a passthrough.
 */
export function usePaywallPage(): PaywallPageContextValue | null {
  return useContext(PaywallPageContext);
}

/**
 * The gate registry, read by `PaywallGate`. Non-throwing like
 * `usePaywallPage`: a gate rendered outside a provider (stories, isolated
 * tests) simply has nothing to register with.
 */
export function usePaywallGateRegistry(): PaywallGateRegistryValue | null {
  return useContext(PaywallGateRegistryContext);
}

export default PaywallProvider;
